import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { apiClient } from '../services/apiClient';

export type ChatAction = { type: 'navigate'; destination: 'home' | 'tenants' | 'showrooms' | 'releases' | 'mqtt' | 'ai' | 'insights' | 'access' } | { type: 'view'; view: 'tenant_list' | 'fleet_report'; title: string; filter?: 'all' | 'attention' | 'active' | 'setup' };
export interface ChatMessage { role: 'user' | 'assistant'; content: string; action?: ChatAction }
export interface ChatSessionSummary { id: string; title: string; updatedAt: string; revision: number }
interface ChatSession extends ChatSessionSummary { messages: ChatMessage[] }
interface ChatStatus { enabled: boolean; configured: boolean; model?: string; modelLabel?: string }
interface ViewState { id: string | null; revision: number; messages: ChatMessage[]; draft: string; pendingPrompt: string; pending: boolean; sessionLoading: boolean; error: string; loadFailed: boolean }
const blank = (): ViewState => ({ id: null, revision: 0, messages: [], draft: '', pendingPrompt: '', pending: false, sessionLoading: false, error: '', loadFailed: false });
const base = '/api/provider/ai/chat';

function failureText(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })?.response;
  if (typeof response?.data?.message === 'string') return response.data.message;
  if (response?.status === 401 || response?.status === 403) return 'Chat access is unavailable. Check your permissions or sign in again.';
  return 'Could not complete this request. Your draft is preserved; try again.';
}

export interface InfinityChatValue {
  open: boolean; setOpen: (open: boolean) => void;
  recentSessions: ChatSessionSummary[]; activeSessionId: string | null; messages: ChatMessage[];
  draft: string; setDraft: (draft: string) => void;
  pending: boolean; pendingPrompt: string; error: string;
  status: ChatStatus | null; statusLoading: boolean; statusError: boolean;
  sessionLoading: boolean; sessionLoadFailed: boolean; sessionsError: boolean;
  refreshStatus: () => Promise<ChatStatus | null>; refreshSessions: () => Promise<void>;
  startNewChat: (prompt?: string) => Promise<void>; openSession: (id: string) => Promise<void>;
  send: (content?: string) => Promise<void>; cancel: () => void;
  replayAction: (action: ChatAction) => void;
}
const Context = createContext<InfinityChatValue | null>(null);

export function InfinityChatProvider({ children, onAction, enabled = true }: { children: React.ReactNode; onAction?: (action: ChatAction) => void; enabled?: boolean }) {
  const [open, setOpenState] = useState(false);
  const openRef = useRef(false);
  const openSessionRef = useRef<((id: string) => Promise<void>) | null>(null);
  const refreshStatusRef = useRef<(() => Promise<ChatStatus | null>) | null>(null);
  const showPanel = useCallback(() => { openRef.current = true; setOpenState(true); }, []);
  const [view, setViewState] = useState<ViewState>(blank);
  const current = useRef(view);
  const [recentSessions, setRecentSessions] = useState<ChatSessionSummary[]>([]);
  const [sessionsError, setSessionsError] = useState(false);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const statusRef = useRef<ChatStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const active = useRef<AbortController | null>(null);
  const statusRequest = useRef<AbortController | null>(null);
  const recentRequest = useRef<AbortController | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const drafts = useRef(new Map<string, string>());
  const actionRef = useRef(onAction);
  actionRef.current = onAction;
  const uncertain = useRef(new Map<string, { content: string; revision: number }>());
  const createKey = useRef('');
  const change = useCallback((patch: Partial<ViewState>) => {
    current.current = { ...current.current, ...patch };
    setViewState(current.current);
  }, []);
  const setDraft = useCallback((value: string) => {
    const draft = value.slice(0, 4000);
    drafts.current.set(current.current.id || 'new', draft);
    change({ draft });
  }, [change]);
  const cancel = useCallback(() => {
    active.current?.abort();
    active.current = null;
    if (current.current.pendingPrompt) setDraft(current.current.pendingPrompt);
    change({ pending: false, pendingPrompt: '', sessionLoading: false });
  }, [change, setDraft]);
  const setOpen = useCallback((value: boolean) => {
    const wasClosed = !openRef.current;
    if (!value) cancel();
    openRef.current = value && enabledRef.current;
    setOpenState(value && enabledRef.current);
    if (value && enabledRef.current && wasClosed) void refreshStatusRef.current?.();
    if (value && enabledRef.current && wasClosed && current.current.id) void openSessionRef.current?.(current.current.id);
  }, [cancel]);
  const refreshStatus = useCallback(async () => {
    if (!enabledRef.current) return null;
    statusRequest.current?.abort();
    const controller = new AbortController();
    statusRequest.current = controller;
    setStatusLoading(true); setStatusError(false);
    try {
      const { data } = await apiClient.get<ChatStatus>(`${base}/status`, { signal: controller.signal });
      if (controller.signal.aborted || !enabledRef.current) return null;
      statusRef.current = data; setStatus(data); return data;
    } catch {
      if (!controller.signal.aborted) { statusRef.current = null; setStatus(null); setStatusError(true); }
      return null;
    } finally { if (!controller.signal.aborted) setStatusLoading(false); }
  }, []);
  const refreshSessions = useCallback(async () => {
    if (!enabledRef.current) return;
    recentRequest.current?.abort();
    const controller = new AbortController();
    recentRequest.current = controller;
    try {
      const { data } = await apiClient.get<{ sessions: ChatSessionSummary[] }>(`${base}/sessions`, { signal: controller.signal });
      if (!controller.signal.aborted && enabledRef.current) { setRecentSessions(data.sessions.slice(0, 3)); setSessionsError(false); }
    } catch { if (!controller.signal.aborted) setSessionsError(true); }
  }, []);
  refreshStatusRef.current = refreshStatus;
  useEffect(() => {
    if (enabled) { void refreshStatus(); void refreshSessions(); }
    else {
      cancel(); statusRequest.current?.abort(); recentRequest.current?.abort();
      change(blank()); openRef.current = false; setOpenState(false); setRecentSessions([]); drafts.current.clear();
      uncertain.current.clear(); createKey.current = ''; statusRef.current = null; setStatus(null);
      setStatusLoading(false); setStatusError(false); setSessionsError(false);
    }
    return () => { active.current?.abort(); statusRequest.current?.abort(); recentRequest.current?.abort(); };
  }, [enabled, refreshStatus, refreshSessions, cancel, change]);

  const applySession = useCallback((session: ChatSession) => {
    let draft = drafts.current.get(session.id) || '';
    const attempt = uncertain.current.get(session.id);
    // Revisions count committed pairs and history is never truncated before the
    // server's full-session limit. Later turns must not hide our committed pair.
    const committedPair = attempt ? session.messages.slice(attempt.revision * 2, attempt.revision * 2 + 2) : [];
    if (attempt && session.revision > attempt.revision && committedPair[0]?.role === 'user' && committedPair[0].content === attempt.content && committedPair[1]?.role === 'assistant') {
      if (draft === attempt.content) { draft = ''; drafts.current.set(session.id, ''); }
      uncertain.current.delete(session.id);
    }
    change({ id: session.id, revision: session.revision, messages: session.messages, draft, loadFailed: false });
  }, [change]);
  const openSession = useCallback(async (id: string) => {
    if (!enabledRef.current) return;
    cancel();
    const controller = new AbortController(); active.current = controller;
    showPanel();
    void refreshStatus();
    change({ id, messages: [], draft: drafts.current.get(id) || '', error: '', sessionLoading: true, loadFailed: true });
    try {
      const { data } = await apiClient.get<{ session: ChatSession }>(`${base}/sessions/${encodeURIComponent(id)}`, { signal: controller.signal });
      if (controller.signal.aborted || active.current !== controller) return;
      applySession(data.session);
    } catch (error) { if (!controller.signal.aborted) change({ error: failureText(error), loadFailed: true }); }
    finally { if (active.current === controller) { active.current = null; change({ sessionLoading: false }); } }
  }, [cancel, change, applySession, showPanel, refreshStatus]);
  openSessionRef.current = openSession;

  const send = useCallback(async (contentArg?: string) => {
    const content = (contentArg ?? current.current.draft).trim().slice(0, 4000);
    if (!enabledRef.current || !content || active.current || current.current.loadFailed) return;
    const controller = new AbortController(); active.current = controller;
    const valid = () => !controller.signal.aborted && active.current === controller && enabledRef.current;
    showPanel(); setDraft(content); change({ pending: true, pendingPrompt: content, error: '' });
    try {
      const availability = await refreshStatus();
      if (!valid()) return;
      if (!availability?.enabled || !availability.configured) { change({ error: 'Chat is unavailable. Check the AI settings before sending.' }); return; }
      if (!current.current.id) {
        if (!createKey.current) createKey.current = typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const { data } = await apiClient.post<{ session: ChatSession }>(`${base}/sessions`, { title: content.slice(0, 80), clientRequestId: createKey.current }, { signal: controller.signal });
        if (!valid()) return;
        drafts.current.set(data.session.id, content);
        applySession(data.session);
        void refreshSessions();
      }
      const id = current.current.id!;
      // A prior timeout/cancel may have committed. Reconcile before retrying, never replay historical actions.
      if (uncertain.current.has(id)) {
        const attempt = uncertain.current.get(id)!;
        const { data } = await apiClient.get<{ session: ChatSession }>(`${base}/sessions/${encodeURIComponent(id)}`, { signal: controller.signal });
        if (!valid()) return;
        applySession(data.session);
        if (!uncertain.current.has(id) && attempt.content === content) return;
        uncertain.current.delete(id);
        if (data.session.revision > attempt.revision) {
          change({ error: 'This conversation changed. Review the latest messages before sending again.' });
          return;
        }
      }
      const revision = current.current.revision;
      uncertain.current.set(id, { content, revision });
      const { data } = await apiClient.post<{ reply: string; model: string; action?: ChatAction; session: ChatSession }>(`${base}/sessions/${encodeURIComponent(id)}/messages`, { content, expectedRevision: revision }, { signal: controller.signal });
      if (!valid()) return;
      uncertain.current.delete(id);
      drafts.current.set(id, '');
      applySession(data.session);
      void refreshSessions();
      if (data.action && valid()) actionRef.current?.(data.action);
    } catch (error: unknown) {
      if (!valid()) return;
      const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
      if (response?.data?.error === 'SESSION_FULL') { change({ error: failureText(error) }); return; }
      if (response?.status === 409 && current.current.id) {
        try {
          const { data } = await apiClient.get<{ session: ChatSession }>(`${base}/sessions/${encodeURIComponent(current.current.id)}`, { signal: controller.signal });
          if (!valid()) return;
          applySession(data.session);
          change({ error: 'This conversation changed. Review the latest messages before sending again.' });
        } catch { if (valid()) change({ error: 'Could not reload the updated conversation. Reopen it before retrying.', loadFailed: true }); }
      } else change({ error: failureText(error) });
    } finally { if (active.current === controller) { active.current = null; change({ pending: false, pendingPrompt: '' }); } }
  }, [applySession, change, refreshSessions, refreshStatus, setDraft, showPanel]);
  const startNewChat = useCallback(async (prompt?: string) => {
    if (!enabledRef.current) return;
    cancel(); createKey.current = ''; change(blank()); showPanel();
    if (prompt?.trim()) { setDraft(prompt); await send(prompt); }
    else void refreshStatus();
  }, [cancel, change, send, setDraft, showPanel, refreshStatus]);
  const replayAction = useCallback((action: ChatAction) => { if (enabledRef.current) actionRef.current?.(action); }, []);

  const value: InfinityChatValue = { open: enabled && open, setOpen, recentSessions: enabled ? recentSessions : [], activeSessionId: enabled ? view.id : null, messages: enabled ? view.messages : [], draft: enabled ? view.draft : '', setDraft, pending: view.pending, pendingPrompt: view.pendingPrompt, error: view.error, status: enabled ? status : null, statusLoading, statusError, sessionLoading: view.sessionLoading, sessionLoadFailed: view.loadFailed, sessionsError, refreshStatus, refreshSessions, startNewChat, openSession, send, cancel, replayAction };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useInfinityChat(): InfinityChatValue {
  const value = useContext(Context);
  if (!value) throw new Error('useInfinityChat requires InfinityChatProvider');
  return value;
}
